import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  createdAt: Date;
  actionUrl?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'read' | 'createdAt'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { user } = useAuth();
  const channelsRef = useRef<any[]>([]);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const maxReconnectAttempts = 3;
  const reconnectAttempts = useRef(0);

  const unreadCount = notifications.filter(n => !n.read).length;

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'read' | 'createdAt'>) => {
    try {
      const newNotification: Notification = {
        ...notification,
        id: `notification-${Date.now()}-${Math.random()}`,
        read: false,
        createdAt: new Date(),
      };
      
      setNotifications(prev => {
        // Prevent duplicate notifications
        const exists = prev.some(n => 
          n.title === newNotification.title && 
          n.message === newNotification.message &&
          Math.abs(n.createdAt.getTime() - newNotification.createdAt.getTime()) < 5000
        );
        
        if (exists) return prev;
        
        return [newNotification, ...prev.slice(0, 99)]; // Keep max 100 notifications
      });
      
      // Show toast for new notifications
      toast({
        title: newNotification.title,
        description: newNotification.message,
        variant: newNotification.type === 'error' ? 'destructive' : 'default'
      });
    } catch (error) {
      console.error('Error adding notification:', error);
    }
  }, []);

  const markAsRead = useCallback((id: string) => {
    try {
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === id ? { ...notification, read: true } : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(() => {
    try {
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    try {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    } catch (error) {
      console.error('Error removing notification:', error);
    }
  }, []);

  // Cleanup function to properly remove channels
  const cleanupChannels = useCallback(() => {
    console.log('Cleaning up notification channels');
    channelsRef.current.forEach(channel => {
      if (channel) {
        try {
          supabase.removeChannel(channel);
        } catch (error) {
          console.error('Error removing channel:', error);
        }
      }
    });
    channelsRef.current = [];
  }, []);

  // Setup real-time listeners with improved error handling
  const setupListeners = useCallback(async () => {
    if (!user?.id) {
      console.log('No authenticated user, skipping notification listeners');
      return;
    }

    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Clean up existing channels first
    cleanupChannels();

    console.log('Setting up notification listeners for user:', user.id);

    try {
      // Test Supabase connection before setting up listeners (silently)
      const { error: connectionError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .limit(1);

      if (connectionError) {
        console.error('Supabase connection test failed:', connectionError);
        // Don't show error notification to user for connection issues
        return;
      }

      // Listen for new post comments
      const postCommentChannel = supabase
        .channel(`post_comments_notifications_${user.id}_${Date.now()}`)
        .on('postgres_changes', 
          { 
            event: 'INSERT', 
            schema: 'public', 
            table: 'post_comments'
          },
          async (payload) => {
            try {
              console.log('New comment detected:', payload);
              
              // Check if this comment is on the current user's post
              const { data: postData, error: postError } = await supabase
                .from('posts')
                .select('author_id, title')
                .eq('id', payload.new.post_id)
                .maybeSingle();

              if (postError) {
                console.error('Error fetching post data:', postError);
                return;
              }

              // Only notify if it's the user's post and they didn't comment themselves
              if (postData?.author_id === user.id && payload.new.author_id !== user.id) {
                console.log('Adding comment notification for user post');
                
                // Fetch commenter profile for the notification
                const { data: commenterProfile } = await supabase
                  .from('profiles')
                  .select('first_name, last_name')
                  .eq('id', payload.new.author_id)
                  .maybeSingle();

                const commenterName = commenterProfile 
                  ? `${commenterProfile.first_name || ''} ${commenterProfile.last_name || ''}`.trim()
                  : 'Someone';

                addNotification({
                  title: 'New Comment',
                  message: `${commenterName} commented on your post "${postData.title}"`,
                  type: 'info',
                  actionUrl: '/discussions'
                });
              }
            } catch (error) {
              console.error('Error processing comment notification:', error);
            }
          }
        );

      // Subscribe with enhanced error handling (but don't show setup errors to users)
      postCommentChannel.subscribe((status, err) => {
        console.log('Comment notification subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('Comment notifications subscribed successfully');
          reconnectAttempts.current = 0; // Reset reconnect attempts on success
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Comment notification subscription error:', err);
          // Don't show setup error to user
          
          // Attempt reconnection if under max attempts
          if (reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current++;
            console.log(`Attempting to reconnect (${reconnectAttempts.current}/${maxReconnectAttempts})`);
            reconnectTimeoutRef.current = setTimeout(() => {
              setupListeners();
            }, 5000 * reconnectAttempts.current); // Exponential backoff
          }
        } else if (status === 'CLOSED') {
          console.log('Comment notification channel closed');
        }
      });

      channelsRef.current.push(postCommentChannel);

      // Listen for new post likes
      const postLikeChannel = supabase
        .channel(`post_likes_notifications_${user.id}_${Date.now()}`)
        .on('postgres_changes', 
          { 
            event: 'INSERT', 
            schema: 'public', 
            table: 'post_likes'
          },
          async (payload) => {
            try {
              console.log('New like detected:', payload);
              
              // Check if this like is on the current user's post
              const { data: postData } = await supabase
                .from('posts')
                .select('author_id, title')
                .eq('id', payload.new.post_id)
                .maybeSingle();

              // Only notify if it's the user's post and they didn't like it themselves
              if (postData?.author_id === user.id && payload.new.user_id !== user.id) {
                console.log('Adding like notification for user post');
                
                // Fetch liker profile for the notification
                const { data: likerProfile } = await supabase
                  .from('profiles')
                  .select('first_name, last_name')
                  .eq('id', payload.new.user_id)
                  .maybeSingle();

                const likerName = likerProfile 
                  ? `${likerProfile.first_name || ''} ${likerProfile.last_name || ''}`.trim()
                  : 'Someone';

                addNotification({
                  title: 'New Like',
                  message: `${likerName} liked your post "${postData.title}"`,
                  type: 'info',
                  actionUrl: '/discussions'
                });
              }
            } catch (error) {
              console.error('Error processing like notification:', error);
            }
          }
        );

      postLikeChannel.subscribe((status, err) => {
        console.log('Like notification subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('Like notifications subscribed successfully');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Like notification subscription error:', err);
          // Don't show setup error to users
        }
      });

      channelsRef.current.push(postLikeChannel);

      // Listen for friend request notifications
      const friendRequestChannel = supabase
        .channel(`friend_requests_notifications_${user.id}_${Date.now()}`)
        .on('postgres_changes', 
          { 
            event: 'INSERT', 
            schema: 'public', 
            table: 'notifications',
            filter: `recipient_id=eq.${user.id} AND type=eq.friend_request`
          },
          async (payload) => {
            try {
              console.log('New friend request notification detected:', payload);
              
              // Fetch requester profile for the notification
              const { data: requesterProfile } = await supabase
                .from('profiles')
                .select('first_name, last_name')
                .eq('id', payload.new.actor_id)
                .maybeSingle();

              const requesterName = requesterProfile 
                ? `${requesterProfile.first_name || ''} ${requesterProfile.last_name || ''}`.trim()
                : 'Someone';

              addNotification({
                title: 'New Friend Request',
                message: `${requesterName} sent you a friend request`,
                type: 'info',
                actionUrl: '/user-browse'
              });
            } catch (error) {
              console.error('Error processing friend request notification:', error);
            }
          }
        );

      friendRequestChannel.subscribe((status, err) => {
        console.log('Friend request notification subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('Friend request notifications subscribed successfully');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Friend request notification subscription error:', err);
          // Don't show setup error to users
        }
      });

      channelsRef.current.push(friendRequestChannel);

      // Don't add any success notification after setup - keep it silent

    } catch (error) {
      console.error('Error setting up notification listeners:', error);
      // Don't show setup errors to users - keep the experience clean
    }
  }, [user?.id, addNotification, cleanupChannels]);

  // Set up listeners when user changes
  useEffect(() => {
    if (user?.id) {
      setupListeners();
    } else {
      cleanupChannels();
      setNotifications([]); // Clear notifications when user logs out
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      cleanupChannels();
    };
  }, [user?.id, setupListeners]);

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
